import React, { useEffect, useState } from 'react'
import {
  Button,
  Form as BForm,
  InputGroup,
  Spinner
  , Tab, Tabs
} from '@themesberg/react-bootstrap'
import { Formik, Form, ErrorMessage } from 'formik'

import { SuperAdminRoutes, TenantRoutes } from '../../../../routes'
import { createCurrencySchema } from '../schema'
import useCreateCurrencies from '../hooks/useCreateCurriences'
import CountryTable from '../../../../components/CountryTable'
import { checkIsTenant } from '../../../../utils/constants'
import { getItem } from '../../../../utils/storageUtils'
import { useDispatch, useSelector } from 'react-redux'
import Loader from '../../../../shared/Loader'
import { getTenantEligibleCurrencies } from '../../../../utils/currency'
import { getAllCurrenciesStart, getAllTenantCurrenciesStart } from '../../../../store/redux-slices/currencies'

const CreateCurrencies = ({
  name: editName,
  code: editCode,
  exchangeRate: editExchangeRate,
  symbol: editSymbol,
  type: editType,
  currencyId,
  isPrimary: editIsPrimary,
  loyaltyPoint: editLoyaltyPoint
}) => {
  const { allCurrencies, allTenantCurrencies, loading: isLoadingCurrencies } = useSelector((state) => state.currencies)
  const dispatch = useDispatch()
  const isTenant = checkIsTenant()
  const [eligibleCurrencies, setEligibleCurrencies] = useState([])
  const {
    search,
    adminId,
    loading,
    navigate,
    tenantId,
    setSearch,
    selectedTab,
    myCountries,
    editCurrency,
    setSelectedTab,
    createCurrencies,
    selectedCountries,
    handleCountrySelect,
    setSelectedCountries,
    getCurrencyById,
    currency
  } = useCreateCurrencies()

  useEffect(() => {
    if (isTenant) {
    // This API fetches all the currencies associated with the tenant.
      dispatch(getAllCurrenciesStart({
        checkTenant: true
      }))
      // This API fetches all the available currencies.
      dispatch(getAllTenantCurrenciesStart({ limit: '', pageNo: '' }))
    }
  }, [isTenant])

  useEffect(() => {
    if (isTenant) {
      const tenantEligibleCurrencies = getTenantEligibleCurrencies({
        allCurrencies: allTenantCurrencies,
        tenantCurrencies: allCurrencies
      })
      setEligibleCurrencies(tenantEligibleCurrencies)
    }
  }, [isTenant, allCurrencies?.rows, allTenantCurrencies?.rows])

  useEffect(() => {
    if (currency && currency.code && currency.currencyCountry) {
      setSelectedCountries(currency.currencyCountry[currency.code] || [])
    }
  }, [currency])

  return (
    <>
      {
        (isLoadingCurrencies || loading) && <Loader />
      }
      <h3>
        {currencyId ? 'Edit Currency' : 'Create Currency'}
      </h3>
      <Formik
        enableReinitialize
        initialValues={{
          name: currency?.name || editName || '',
          code: currency?.code || editCode || '',
          symbol: currency?.symbol || editSymbol || '',
          exchangeRate: currency?.exchangeRate || editExchangeRate || '',
          type: currency?.type ?? editType ?? 0,
          loyaltyPoint: currency?.loyaltyPoint ? String(currency.loyaltyPoint) : (editLoyaltyPoint ? String(editLoyaltyPoint) : '0')
        }}
        validationSchema={createCurrencySchema(currency?.name || editName)}
        onSubmit={({ name, code, exchangeRate, symbol, type, loyaltyPoint }) => {
          if (selectedTab !== 'details' || (!checkIsTenant() && !tenantId)) {
            currencyId
              ? editCurrency({
                currencyId,
                name,
                code,
                exchangeRate,
                symbol,
                type: Number(type),
                isPrimary: editIsPrimary,
                loyaltyPoint,
                currencyCountry: {
                  [code]: selectedCountries
                },
                navigate,
                tenantId: checkIsTenant() ? getItem('tenant-id') : tenantId || '',
                adminId
              })
              : createCurrencies({
                name,
                code,
                exchangeRate,
                symbol,
                type: Number(type),
                isPrimary: false,
                loyaltyPoint,
                currencyCountry: {
                  [code]: selectedCountries
                },
                navigate,
                tenantId: checkIsTenant() ? getItem('tenant-id') : tenantId || '',
                adminId
              })
          } else {
            setSelectedTab('country')
          }
        }}
      >
        {({
          touched,
          errors,
          values,
          handleChange,
          handleSubmit,
          handleBlur
        }) => (
          <div>
            <Form>
              <Tabs
                activeKey={selectedTab}
                onSelect={(tab) => setSelectedTab(tab)}
                className='nav-light card-header-tabs'
              >
                <Tab eventKey='details' title='Details' disabled>
                  <div className='mx-2 my-5'>
                    {/* Div for name */}
                    <div className='form-group mb-2'>
                      <label
                        htmlFor='name'
                        className={touched.name && errors.name ? 'text-danger' : ''}
                      >
                        <b>Name</b>
                        <span className='text-danger'> *</span>
                      </label>

                      <InputGroup
                        className={
                      touched.name && errors.name ? 'border border-danger' : ''
                    }
                      >
                        <BForm.Control
                          name='name'
                          required
                          type='text'
                          placeholder='Enter name of Currency'
                          value={values.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </InputGroup>

                      <ErrorMessage
                        component='div'
                        name='name'
                        className='text-danger'
                      />
                    </div>

                    {/* Div for code */}
                    <div className='form-group pt-2 mb-2'>
                      <label
                        htmlFor='code'
                        className={touched.code && errors.code ? 'text-danger' : ''}
                      >
                        <b>Code</b>
                        <span className='text-danger'> *</span>
                      </label>

                      <InputGroup
                        className={
                      touched.code && errors.code ? 'border border-danger' : ''
                    }
                      >
                        {
                      isTenant
                        ? (
                          <BForm.Select
                            name='code'
                            required
                            value={values.code}
                            onChange={(e) => {
                              handleChange(e)
                              const selectedCurrency = eligibleCurrencies.find(cur => cur.code === e.target.value)
                              getCurrencyById(selectedCurrency?.currencyId)
                            }}
                            onBlur={handleBlur}
                            disabled={!!editName}
                          >
                            <option value='' disabled>
                              ---Select Code---
                            </option>
                            {eligibleCurrencies?.map(
                              ({ code }) => (
                                <option key={code} value={code}>
                                  {code}
                                </option>
                              )
                            )}
                          </BForm.Select>
                          )
                        : <BForm.Control
                            name='code'
                            required
                            type='text'
                            placeholder='Enter currency Code'
                            value={values.code}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={!!editName}
                          />
                    }

                      </InputGroup>

                      <ErrorMessage
                        component='div'
                        name='code'
                        className='text-danger'
                      />
                    </div>

                    {/* Div for Symbol */}
                    <div className='form-group pt-2 mb-2'>
                      <label
                        htmlFor='symbol'
                        className={
                      touched.symbol && errors.symbol ? 'text-danger' : ''
                    }
                      >
                        <b>Symbol</b>
                        <span className='text-danger'> * </span>
                      </label>

                      <InputGroup
                        className={
                      touched.symbol && errors.symbol
                        ? 'border border-danger'
                        : ''
                    }
                      >
                        <BForm.Control
                          name='symbol'
                          required
                          type='text'
                          placeholder='Enter currency symbol'
                          value={values.symbol}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </InputGroup>

                      <ErrorMessage
                        component='div'
                        name='symbol'
                        className='text-danger'
                      />
                    </div>

                    {/* Div for exchangeRate */}
                    <div className='form-group pt-2 mb-3'>
                      <label
                        htmlFor='exchangeRate'
                        className={
                      touched.exchangeRate && errors.exchangeRate
                        ? 'text-danger'
                        : ''
                    }
                      >
                        <b>Exchange Rate</b>
                        <span className='text-danger'> *</span>
                        (with base currency Euro)
                      </label>

                      <InputGroup
                        className={
                      touched.exchangeRate && errors.exchangeRate
                        ? 'border border-danger'
                        : ''
                    }
                      >
                        <BForm.Control
                          name='exchangeRate'
                          required
                          type='text'
                          placeholder='Enter currency Exchange Rate'
                          value={values.exchangeRate}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </InputGroup>

                      <ErrorMessage
                        component='div'
                        name='exchangeRate'
                        className='text-danger'
                      />
                    </div>

                    {/* Div for loyaltyPoint */}
                    <div className='form-group pt-2 mb-3'>
                      <label
                        htmlFor='loyaltyPoint'
                        className={
                      touched.loyaltyPoint && errors.loyaltyPoint
                        ? 'text-danger'
                        : ''
                    }
                      >
                        <b>Loyalty Point</b>
                        <span className='text-danger'> *</span>
                      </label>

                      <InputGroup
                        className={
                      touched.loyaltyPoint && errors.loyaltyPoint
                        ? 'border border-danger'
                        : ''
                    }
                      >
                        <BForm.Control
                          name='loyaltyPoint'
                          required
                          type='text'
                          min={0}
                          placeholder='Enter Loyalty Point'
                          value={values.loyaltyPoint}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </InputGroup>

                      <ErrorMessage
                        component='div'
                        name='loyaltyPoint'
                        className='text-danger'
                      />
                    </div>

                    {/* Div for type */}
                    <div className='form-group mb-2'>
                      <label
                        htmlFor='type'
                        className={touched.type && errors.type ? 'text-danger' : ''}
                      >
                        <b>Type</b>
                        <span className='text-danger'> *</span>
                      </label>

                      <InputGroup
                        className={
                      touched.type && errors.type ? 'border border-danger' : ''
                    }
                      >
                        <BForm.Select
                          name='type'
                          value={values.type}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        >
                          <option value='' disabled>
                            ---Select Type---
                          </option>
                          <option value={0}>Crypto</option>
                          <option value={1}>Fiat</option>
                        </BForm.Select>
                      </InputGroup>

                      <ErrorMessage
                        component='div'
                        name='type'
                        className='text-danger'
                      />
                    </div>
                  </div>
                </Tab>

                {(tenantId || checkIsTenant()) &&
                  <Tab eventKey='country' title='Country' disabled>
                    <div className='d-flex justify-content-end align-items-center mt-5 mb-3 mx-2'>
                      <BForm.Control
                        type='text'
                        placeholder='Search Country Name'
                        size='sm'
                        style={{ maxWidth: '230px' }}
                        value={search}
                        onChange={(event) => {
                          setSearch(event.target.value.replace(/[^\w\s\n]/gi, ''))
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') e.preventDefault()
                        }}
                      />
                    </div>
                    <CountryTable
                      loading={loading}
                      myCountries={myCountries}
                      enableEdit={selectedTab !== 'details' || (!checkIsTenant() && !tenantId)}
                      selectedCountries={selectedCountries}
                      handleCountrySelect={handleCountrySelect}
                      setSelectedCountries={setSelectedCountries}
                    />
                  </Tab>}

              </Tabs>
              <div className='mt-4 d-flex justify-content-between align-items-center'>
                <Button
                  variant='outline-warning'
                  onClick={() => selectedTab !== 'details' ? setSelectedTab('details') : navigate(checkIsTenant() ? TenantRoutes.TenantConfigurations : SuperAdminRoutes.Currencies)}
                >
                  {selectedTab !== 'details' ? 'Previous' : 'Cancel'}
                </Button>
                <Button
                  variant='outline-success'
                  onClick={handleSubmit}
                  className='ml-2'
                >
                  {(selectedTab !== 'details' || (!checkIsTenant() && !tenantId)) ? 'Submit' : 'Next'}
                  {loading && (
                    <Spinner
                      as='span'
                      animation='border'
                      size='sm'
                      role='status'
                      aria-hidden='true'
                    />
                  )}
                </Button>
              </div>

            </Form>
          </div>
        )}
      </Formik>
    </>
  )
}

export default CreateCurrencies
